/**
 * Müşteri adayı sözleşmeleri (M3 WP 3.9). Kaynak: keşif tur 2 F01 (`kesif/2026-09-18-tur2/kanit/akis-F01-aday.md`).
 * - 6 durum; değerlendirme sebebi (açıklama) isteğe bağlı; dönüştürme HER durumda yapılabilir (nitelikli değilse uyarı).
 * - Zorunlu: şirket adı, kaynak; ad/soyad zorunlu değil. İYS izin kaynağı isteğe bağlı (M2 kişi alanıyla aynı liste).
 * - Dönüştürme kişi + şirket + fırsat üretir, aday YERİNDE KALIR ve dönüştürülen kayıtlara bağlanır. Castinflow kararı
 *   (K-M3-10): dönüştürme bir kez yapılır (idempotent) — ikinci istek aynı kayıtları döner, çoğaltma üretmez.
 */
import { z } from 'zod';
import { MONEY_POLICY, currencySchema, decimalString } from './decimal.js';
import { IYS_CONSENT_SOURCES } from './crm.js';
const uuid = z.guid();
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'tarih YYYY-AA-GG olmalı');
const text = (max) => z.string().trim().max(max);
const keySchema = z.string().regex(/^[a-z][a-z0-9_]{1,39}$/, 'geçersiz anahtar');
const optText = (max) => z
    .string()
    .trim()
    .max(max)
    .transform((s) => (s === '' ? null : s))
    .nullable();
export const LEAD_STATUSES = [
    'new',
    'evaluating',
    'qualified',
    'unqualified',
    'uncertain',
    'duplicate',
];
export const LEAD_STATUS_LABELS = {
    new: 'Yeni',
    evaluating: 'Değerlendiriliyor',
    qualified: 'Nitelikli',
    unqualified: 'Niteliksiz',
    uncertain: 'Belirsiz',
    duplicate: 'Tekrar eden',
};
/**
 * Pazarlama çalışma merkezi (24 Ekim 2026): adayın DÖNÜŞTÜĞÜ fırsatın sonucu (aday liste alanı `convertedOpportunityStatus`).
 * `OPPORTUNITY_STATUSES` anlamıyla: `open` = açık (aktif ya da beklemede), `won` = kazanıldı, `lost` = kazanılmadan kapandı
 * (kaybedildi, terk edildi ya da iptal edildi). Fırsat çağıranın göremediği, silinmiş ya da hiç yoksa değer BOŞTUR (null).
 */
export const LEAD_CONVERTED_OPPORTUNITY_STATUSES = ['open', 'won', 'lost'];
export const LEAD_CONVERTED_OPPORTUNITY_STATUS_LABELS = {
    open: 'Açık',
    won: 'Kazanıldı',
    lost: 'Kaybedildi',
};
/**
 * Sistem kaynakları (keşif S19). K-M3-16: aday kaynağı kişi/şirket kaynağıyla AYNI tenant listesidir (`record_lookups`
 * türü `source`; sistem değerleri `SYSTEM_LOOKUPS`) — tenant yeni kaynak ekleyebilir, sıralar, pasifleştirir. Bu sabit
 * yalnız sistem değerlerinin anahtar/etiketidir; girdi doğrulaması etkin `source` listesine karşı sunucuda yapılır.
 */
export const LEAD_SOURCES = ['email', 'inbound_call', 'cold_call', 'web_form', 'other'];
export const LEAD_SOURCE_LABELS = {
    email: 'E-posta',
    inbound_call: 'Gelen çağrı',
    cold_call: 'Soğuk arama',
    web_form: 'Web formu',
    other: 'Diğer',
};
const emailSchema = z
    .string()
    .trim()
    .max(254)
    .transform((s) => (s === '' ? null : s))
    .nullable()
    .refine((s) => s === null || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s), 'geçerli bir e-posta girin');
export const leadCoreInputSchema = z
    .object({
    firstName: optText(100).optional(),
    lastName: optText(100).optional(),
    title: optText(120).optional(),
    companyName: text(200).min(1, 'şirket adı gerekli'),
    status: z.enum(LEAD_STATUSES).optional(),
    rating: z.number().int().min(1).max(5).nullable().optional(),
    /** Etkin `source` lookup anahtarı (sunucu doğrular) */
    sourceKey: keySchema,
    email: emailSchema.optional(),
    phone: optText(40).optional(),
    address: optText(1000).optional(),
    taxNo: optText(20).optional(),
    taxOffice: optText(120).optional(),
    iysConsentSource: z.enum(IYS_CONSENT_SOURCES).nullable().optional(),
    iysConsentDate: isoDate.nullable().optional(),
    details: optText(10000).optional(),
    ownerId: uuid.optional(),
})
    .strict();
export const createLeadInputSchema = z
    .object({
    core: leadCoreInputSchema,
    customValues: z.record(z.string(), z.unknown()).prefault({}),
})
    .strict();
export const updateLeadInputSchema = z
    .object({ core: leadCoreInputSchema.omit({ status: true }).partial() })
    .strict();
/** Değerlendir: yeni durum + isteğe bağlı açıklama (keşif: sebep zorunlu değil). */
export const leadStatusInputSchema = z
    .object({
    status: z.enum(LEAD_STATUSES),
    /** Değerlendirme sebebi (S20; tenant tanımlı, etkin olmalı) — isteğe bağlı */
    reasonKey: keySchema.nullable().optional(),
    reasonDetail: optText(2000).optional(),
})
    .strict();
export const convertLeadInputSchema = z
    .object({
    /** Kişi oluştur (ad veya soyad varsa); false → kişi üretilmez */
    createContact: z.boolean().default(true),
    /** Şirket: yeni (aday şirket adından), mevcut (görünür şirket) veya yok */
    company: z.discriminatedUnion('mode', [
        z.object({ mode: z.literal('new') }).strict(),
        z.object({ mode: z.literal('existing'), companyId: uuid }).strict(),
        z.object({ mode: z.literal('none') }).strict(),
    ]),
    /** Fırsat (verilmezse üretilmez) */
    opportunity: z
        .object({
        name: text(200).min(1, 'konu gerekli').optional(),
        stageKey: z
            .string()
            .regex(/^[a-z][a-z0-9_]{1,39}$/)
            .optional(),
        amount: decimalString(MONEY_POLICY).optional(),
        currency: currencySchema.optional(),
        probability: z.number().int().min(0).max(100).nullable().optional(),
        expectedCloseDate: isoDate.nullable().optional(),
    })
        .strict()
        .nullable()
        .default(null),
    /** Adayın e-posta/telefonunu yeni şirkete de ekle */
    copyContactInfoToCompany: z.boolean().default(true),
    /** Yeni şirkette şirket adını işletme adı olarak da kullan */
    useCompanyNameAsTradeName: z.boolean().default(false),
    /** Nitelikli olmayan aday için açık onay (uyarı görüldü) */
    confirmUnqualified: z.boolean().default(false),
})
    .strict();
export const leadProfileSchema = z.object({
    kind: z.literal('lead'),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    title: z.string().nullable(),
    companyName: z.string(),
    status: z.enum(LEAD_STATUSES),
    statusChangedAt: z.string(),
    statusReasonKey: z.string().nullable(),
    statusReasonDetail: z.string().nullable(),
    rating: z.number().nullable(),
    sourceKey: z.string(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    address: z.string().nullable(),
    taxNo: z.string().nullable(),
    taxOffice: z.string().nullable(),
    iysConsentSource: z.string().nullable(),
    iysConsentDate: z.string().nullable(),
    details: z.string().nullable(),
    convertedAt: z.string().nullable(),
    convertedContactId: z.string().nullable(),
    convertedCompanyId: z.string().nullable(),
    convertedOpportunityId: z.string().nullable(),
});
export const leadConversionResultSchema = z.object({
    created: z.boolean(),
    contactId: z.string().nullable(),
    companyId: z.string().nullable(),
    opportunityId: z.string().nullable(),
});
//# sourceMappingURL=leads.js.map